import React, { useState, useEffect, useRef, useCallback } from 'react';
import { videoAPI } from '../utils/api';
import CreditPurchase from './CreditPurchase';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { toast } from 'sonner';
import {
  Video, Sparkles, Download, Trash2, ArrowLeft, Clock,
  Film, Megaphone, Heart, Flame, Loader2,
  ChevronDown, RefreshCw, AlertCircle, Coins,
  Globe, Lock, Eye, Crown, Award, ShoppingCart, Share2
} from 'lucide-react';
import { track } from '../utils/firebase';

const TEMPLATES = [
  { id: null, label: 'Custom', icon: Sparkles, color: '#4338CA', desc: 'Your own prompt' },
  { id: 'reels', label: 'Reels', icon: Film, color: '#E11D48', desc: 'Short-form social' },
  { id: 'ads', label: 'Ads', icon: Megaphone, color: '#0891B2', desc: 'Advertisements' },
  { id: 'spiritual', label: 'Spiritual', icon: Heart, color: '#7C3AED', desc: 'Peaceful & calm' },
  { id: 'motivational', label: 'Motivational', icon: Flame, color: '#EA580C', desc: 'Inspiring' },
];

const ASPECT_RATIOS = [
  { value: '16:9', label: '16:9' },
  { value: '9:16', label: '9:16' },
  { value: '1:1', label: '1:1' },
];

const AIVideoGenerator = ({ onBack, onOpenGallery, onOpenLeaderboard, initialPrompt, initialTemplate }) => {
  const [prompt, setPrompt] = useState(initialPrompt || '');
  const [selectedTemplate, setSelectedTemplate] = useState(initialTemplate || null);
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [isPublic, setIsPublic] = useState(false);
  const [credits, setCredits] = useState(null);
  const [history, setHistory] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [activeJobId, setActiveJobId] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [showBuyCredits, setShowBuyCredits] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    loadCredits();
    loadHistory();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const loadCredits = async () => {
    try {
      const res = await videoAPI.getCredits();
      setCredits(res.data);
    } catch { /* ignore */ }
  };

  const loadHistory = async () => {
    try {
      const res = await videoAPI.getHistory();
      setHistory(res.data);
      const inProgress = res.data.find(v => v.status === 'processing');
      if (inProgress) {
        setActiveJobId(inProgress.id);
        setGenerating(true);
        startPolling(inProgress.id);
      }
    } catch { /* ignore */ }
  };

  const startPolling = useCallback((videoId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await videoAPI.getStatus(videoId);
        const video = res.data;
        setHistory(prev => prev.map(v => v.id === videoId ? video : v));
        if (video.status === 'succeeded' || video.status === 'failed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setGenerating(false);
          setActiveJobId(null);
          loadCredits();
          if (video.status === 'succeeded') toast.success('Video generated!');
          else toast.error(video.error || 'Generation failed');
        }
      } catch {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setGenerating(false);
      }
    }, 4000);
  }, []);

  const handleGenerate = async () => {
    if (!prompt.trim()) { toast.error('Enter a prompt'); return; }
    if (credits && credits.credits < 1) { setShowBuyCredits(true); return; }
    if (credits && credits.tier === 'free' && credits.daily_count >= credits.daily_limit) {
      toast.error(`Daily limit reached (${credits.daily_limit}/day). Upgrade for unlimited!`);
      setShowBuyCredits(true);
      return;
    }
    setGenerating(true);
    try {
      const res = await videoAPI.generate({
        prompt: prompt.trim(),
        template: selectedTemplate,
        duration: credits?.max_duration || 5,
        aspect_ratio: aspectRatio,
        is_public: isPublic,
      });
      const video = res.data;
      setActiveJobId(video.id);
      setHistory(prev => [video, ...prev]);
      loadCredits();
      startPolling(video.id);
      track.generateVideo(prompt.trim());
      toast.success('Generation started! ~1-3 minutes.');
    } catch (err) {
      setGenerating(false);
      const detail = err.response?.data?.detail || 'Failed';
      if (err.response?.status === 429) {
        toast.error(detail);
      } else if (err.response?.status === 402) {
        setShowBuyCredits(true);
        toast.error(detail);
      } else {
        toast.error(detail);
      }
    }
  };

  const handleDelete = async (videoId) => {
    try {
      await videoAPI.deleteVideo(videoId);
      setHistory(prev => prev.filter(v => v.id !== videoId));
      toast.success('Deleted');
    } catch { toast.error('Failed'); }
  };

  const handleToggleVisibility = async (videoId) => {
    try {
      const res = await videoAPI.toggleVisibility(videoId);
      setHistory(prev => prev.map(v => v.id === videoId ? { ...v, is_public: res.data.is_public } : v));
      toast.success(res.data.is_public ? 'Shared to gallery' : 'Made private');
    } catch { toast.error('Failed'); }
  };

  const handleDownload = (url, p) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = `callchat-ai-${p.slice(0, 30).replace(/\s+/g, '-')}.mp4`;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.click();
  };

  const [shareMenuId, setShareMenuId] = useState(null);

  const handleShare = async (video, platform) => {
    setShareMenuId(null);
    const shareText = `Check out this AI-generated video: "${video.prompt.slice(0, 80)}" - Made with CallChat AI`;
    const shareUrl = video.video_url;
    const webUrl = `https://callchat.online`;

    if (platform === 'native' && navigator.share) {
      try {
        await navigator.share({ title: 'CallChat AI Video', text: shareText, url: shareUrl });
        toast.success('Shared!');
      } catch (e) { if (e.name !== 'AbortError') toast.error('Share failed'); }
      return;
    }

    const urls = {
      whatsapp: `https://api.whatsapp.com/send?text=${encodeURIComponent(shareText + '\n\n' + shareUrl)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`,
      instagram: null, // Instagram doesn't support URL sharing — use native share or download
      copy: null,
    };

    if (platform === 'instagram') {
      // Download video for Instagram sharing
      handleDownload(shareUrl, video.prompt);
      toast.success('Video downloaded! Open Instagram and upload from your gallery.');
      return;
    }

    if (platform === 'copy') {
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied!');
      } catch { toast.error('Copy failed'); }
      return;
    }

    if (urls[platform]) {
      window.open(urls[platform], '_blank', 'noopener,noreferrer');
    }
  };

  const isFree = credits?.tier === 'free';
  const dailyLeft = credits ? (credits.daily_limit - credits.daily_count) : null;

  return (
    <div className="flex flex-col h-full bg-[#F8FAFC]" data-testid="ai-video-generator">
      {/* Header */}
      <div className="sticky top-0 z-10 flex items-center gap-3 px-4 py-3 bg-white border-b border-[#E2E8F0] shadow-sm">
        <Button variant="ghost" size="icon" onClick={onBack} data-testid="ai-video-back">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#4338CA] to-[#7C3AED] flex items-center justify-center shrink-0">
            <Video className="w-5 h-5 text-white" />
          </div>
          <div className="min-w-0">
            <h1 className="font-bold text-[#0F172A] text-lg leading-tight">AI Video Studio</h1>
            <p className="text-xs text-[#64748B] truncate">
              {isFree ? 'Free' : 'Pro'} &#183; {credits?.max_duration || 5}s max
            </p>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-1.5 shrink-0">
          <Button variant="outline" size="sm" onClick={onOpenLeaderboard} className="text-xs gap-1 hidden sm:flex" data-testid="open-leaderboard-btn">
            <Award className="w-3.5 h-3.5" /> Ranks
          </Button>
          <Button variant="outline" size="sm" onClick={onOpenGallery} className="text-xs gap-1" data-testid="open-gallery-btn">
            <Eye className="w-3.5 h-3.5" /> Explore
          </Button>
          <button onClick={() => setShowBuyCredits(true)} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#FFF7ED] border border-[#FDBA74] rounded-full hover:bg-[#FFEDD5] transition-colors" data-testid="credit-balance">
            <Coins className="w-4 h-4 text-[#EA580C]" />
            <span className="text-sm font-semibold text-[#EA580C]">{credits?.credits ?? '...'}</span>
          </button>
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="max-w-2xl mx-auto p-4 space-y-4">

          {/* Free tier upgrade banner */}
          {isFree && (
            <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-[#FFF7ED] to-[#FFFBEB] border border-[#FDBA74] rounded-xl" data-testid="upgrade-banner">
              <Crown className="w-5 h-5 text-[#EA580C] shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#92400E]">
                  Free plan: {dailyLeft !== null ? `${dailyLeft}/${credits.daily_limit} generations left today` : 'Loading...'}
                </p>
                <p className="text-xs text-[#B45309]">Videos include watermark. Upgrade for unlimited + 10s videos + no watermark.</p>
              </div>
              <Button size="sm" onClick={() => setShowBuyCredits(true)} className="bg-[#EA580C] hover:bg-[#C2410C] text-white text-xs shrink-0" data-testid="upgrade-btn">
                Upgrade
              </Button>
            </div>
          )}

          {/* Templates */}
          <div>
            <label className="text-sm font-medium text-[#334155] mb-2 block">Template</label>
            <div className="grid grid-cols-5 gap-2" data-testid="template-grid">
              {TEMPLATES.map(t => {
                const Icon = t.icon;
                const sel = selectedTemplate === t.id;
                return (
                  <button key={t.id || 'custom'} onClick={() => setSelectedTemplate(t.id)}
                    className={`flex flex-col items-center gap-1.5 p-2.5 rounded-xl border-2 transition-all ${sel ? 'border-[#4338CA] bg-[#EEF2FF] shadow-md' : 'border-[#E2E8F0] bg-white hover:border-[#CBD5E1]'}`}
                    data-testid={`template-${t.id || 'custom'}`}>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: sel ? t.color : '#F1F5F9' }}>
                      <Icon className="w-4 h-4" style={{ color: sel ? '#FFF' : t.color }} />
                    </div>
                    <span className="text-[11px] font-medium text-[#334155]">{t.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Prompt */}
          <div>
            <label className="text-sm font-medium text-[#334155] mb-2 block">Describe your video</label>
            <textarea value={prompt} onChange={e => setPrompt(e.target.value)}
              placeholder={selectedTemplate === 'reels' ? 'e.g., A cat in sunglasses walking through neon streets...'
                : selectedTemplate === 'ads' ? 'e.g., Sleek smartwatch rotating with golden lighting...'
                : selectedTemplate === 'spiritual' ? 'e.g., Sunrise over misty mountains with flowing river...'
                : selectedTemplate === 'motivational' ? 'e.g., Runner sprinting through rain in slow motion...'
                : 'e.g., A futuristic cityscape at sunset with flying cars...'}
              className="w-full h-24 px-4 py-3 text-sm border-2 border-[#E2E8F0] rounded-xl bg-white focus:border-[#4338CA] focus:ring-1 focus:ring-[#4338CA] outline-none resize-none transition-colors"
              maxLength={1000} data-testid="video-prompt-input" />
            <p className="text-xs text-[#94A3B8] mt-1">{prompt.length}/1000</p>
          </div>

          {/* Aspect Ratio + Public toggle */}
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <label className="text-sm font-medium text-[#334155] mb-2 block">Aspect Ratio</label>
              <div className="flex gap-2" data-testid="aspect-ratio-selector">
                {ASPECT_RATIOS.map(ar => (
                  <button key={ar.value} onClick={() => setAspectRatio(ar.value)}
                    className={`px-3 py-1.5 text-sm rounded-lg border-2 transition-all ${aspectRatio === ar.value ? 'border-[#4338CA] bg-[#EEF2FF] text-[#4338CA] font-medium' : 'border-[#E2E8F0] bg-white text-[#475569]'}`}
                    data-testid={`aspect-${ar.value.replace(':', '-')}`}>
                    {ar.label}
                  </button>
                ))}
              </div>
            </div>
            <button onClick={() => setIsPublic(!isPublic)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border-2 text-sm transition-all ${isPublic ? 'border-[#4338CA] bg-[#EEF2FF] text-[#4338CA]' : 'border-[#E2E8F0] bg-white text-[#475569]'}`}
              data-testid="public-toggle">
              {isPublic ? <Globe className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
              {isPublic ? 'Public' : 'Private'}
            </button>
          </div>

          {/* Generate */}
          <Button onClick={handleGenerate} disabled={generating || !prompt.trim()}
            className="w-full h-12 bg-gradient-to-r from-[#4338CA] to-[#7C3AED] hover:from-[#3730A3] hover:to-[#6D28D9] text-white font-semibold rounded-xl shadow-lg shadow-indigo-200"
            data-testid="generate-video-btn">
            {generating ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Generating...</>
              : <><Sparkles className="w-5 h-5 mr-2" /> Generate Video (1 credit){isFree ? ' + watermark' : ''}</>}
          </Button>

          {/* Active job */}
          {activeJobId && (
            <div className="flex items-center gap-3 p-4 bg-[#EEF2FF] border border-[#C7D2FE] rounded-xl" data-testid="generation-status">
              <Loader2 className="w-8 h-8 text-[#4338CA] animate-spin shrink-0" />
              <div>
                <p className="text-sm font-medium text-[#312E81]">Generating your video...</p>
                <p className="text-xs text-[#6366F1]">~1-3 minutes. You can wait or check back later.</p>
              </div>
            </div>
          )}

          {/* History */}
          <div>
            <button onClick={() => setShowHistory(!showHistory)}
              className="flex items-center gap-2 text-sm font-medium text-[#334155] mb-3 hover:text-[#4338CA] transition-colors"
              data-testid="toggle-history">
              <Clock className="w-4 h-4" /> History ({history.length})
              <ChevronDown className={`w-4 h-4 transition-transform ${showHistory ? 'rotate-180' : ''}`} />
            </button>

            {showHistory && (
              <div className="space-y-3" data-testid="video-history">
                {history.length === 0 && <p className="text-sm text-[#94A3B8] text-center py-6">No videos yet</p>}
                {history.map(video => (
                  <div key={video.id} className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden shadow-sm" data-testid={`video-card-${video.id}`}>
                    {video.status === 'succeeded' && video.video_url && (
                      <div className="relative bg-black">
                        <video src={video.video_url} controls className="w-full max-h-56 object-contain" preload="metadata" />
                        {video.has_watermark && (
                          <div className="absolute top-2 left-2 px-2 py-0.5 bg-black/50 rounded text-[10px] text-white/70 font-medium">CallChat AI</div>
                        )}
                      </div>
                    )}
                    <div className="p-3">
                      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                        {video.status === 'processing' && <span className="flex items-center gap-1 text-xs font-medium text-[#4338CA] bg-[#EEF2FF] px-2 py-0.5 rounded-full"><Loader2 className="w-3 h-3 animate-spin" /> Processing</span>}
                        {video.status === 'succeeded' && <span className="text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded-full">Done</span>}
                        {video.status === 'failed' && <span className="flex items-center gap-1 text-xs font-medium text-red-700 bg-red-50 px-2 py-0.5 rounded-full"><AlertCircle className="w-3 h-3" /> Failed</span>}
                        {video.template && <span className="text-xs text-[#64748B] bg-[#F8FAFC] px-2 py-0.5 rounded-full border">{video.template}</span>}
                        {video.is_public && <Globe className="w-3.5 h-3.5 text-[#4338CA]" />}
                        {video.has_watermark && <span className="text-[10px] text-[#94A3B8]">watermark</span>}
                        <span className="text-xs text-[#94A3B8] ml-auto">{new Date(video.created_at).toLocaleDateString()}</span>
                      </div>
                      <p className="text-sm text-[#334155] line-clamp-2 mb-2">{video.prompt}</p>
                      {video.status === 'failed' && video.error && <p className="text-xs text-red-600 mb-2">{video.error}</p>}
                      <div className="flex items-center gap-2 flex-wrap">
                        {video.status === 'succeeded' && video.video_url && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => handleDownload(video.video_url, video.prompt)} className="text-xs" data-testid={`download-${video.id}`}>
                              <Download className="w-3.5 h-3.5 mr-1" /> Download
                            </Button>
                            <div className="relative">
                              <Button size="sm" variant="outline" onClick={() => setShareMenuId(shareMenuId === video.id ? null : video.id)} className="text-xs" data-testid={`share-${video.id}`}>
                                <Share2 className="w-3.5 h-3.5 mr-1" /> Share
                              </Button>
                              {shareMenuId === video.id && (
                                <div className="absolute left-0 bottom-full mb-1 bg-white border border-[#E2E8F0] rounded-xl shadow-xl p-2 z-50 min-w-[180px]" data-testid={`share-menu-${video.id}`}>
                                  <button onClick={() => handleShare(video, 'whatsapp')} className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-[#334155] hover:bg-[#F0FDF4] rounded-lg transition-colors" data-testid="share-whatsapp">
                                    <svg className="w-4 h-4 text-[#25D366]" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                                    WhatsApp
                                  </button>
                                  <button onClick={() => handleShare(video, 'facebook')} className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-[#334155] hover:bg-[#EFF6FF] rounded-lg transition-colors" data-testid="share-facebook">
                                    <svg className="w-4 h-4 text-[#1877F2]" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                                    Facebook
                                  </button>
                                  <button onClick={() => handleShare(video, 'instagram')} className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-[#334155] hover:bg-[#FFF7ED] rounded-lg transition-colors" data-testid="share-instagram">
                                    <svg className="w-4 h-4 text-[#E4405F]" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678a6.162 6.162 0 100 12.324 6.162 6.162 0 100-12.324zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405a1.441 1.441 0 11-2.882 0 1.441 1.441 0 012.882 0z"/></svg>
                                    Instagram
                                  </button>
                                  <div className="border-t border-[#E2E8F0] my-1"></div>
                                  {navigator.share && (
                                    <button onClick={() => handleShare(video, 'native')} className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-[#334155] hover:bg-[#F8FAFC] rounded-lg transition-colors" data-testid="share-native">
                                      <Share2 className="w-4 h-4 text-[#4338CA]" /> More Options
                                    </button>
                                  )}
                                  <button onClick={() => handleShare(video, 'copy')} className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-[#334155] hover:bg-[#F8FAFC] rounded-lg transition-colors" data-testid="share-copy-link">
                                    <svg className="w-4 h-4 text-[#64748B]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
                                    Copy Link
                                  </button>
                                </div>
                              )}
                            </div>
                            <Button size="sm" variant="outline" onClick={() => handleToggleVisibility(video.id)} className="text-xs" data-testid={`visibility-${video.id}`}>
                              {video.is_public ? <><Lock className="w-3.5 h-3.5 mr-1" /> Private</> : <><Globe className="w-3.5 h-3.5 mr-1" /> Public</>}
                            </Button>
                          </>
                        )}
                        {video.status === 'processing' && (
                          <Button size="sm" variant="outline" onClick={() => { setActiveJobId(video.id); startPolling(video.id); }} className="text-xs">
                            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Check
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(video.id)} className="text-xs text-red-500 hover:text-red-700 ml-auto" data-testid={`delete-${video.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </ScrollArea>

      {showBuyCredits && (
        <CreditPurchase
          onClose={() => setShowBuyCredits(false)}
          onSuccess={() => { loadCredits(); setShowBuyCredits(false); }}
          currentCredits={credits?.credits}
        />
      )}
    </div>
  );
};

export default AIVideoGenerator;
